
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Message } from "./types";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger('fetchConversation');
const DEFAULT_LIMIT = 20;

// Fetch messages between the current user and another user with pagination support
export const fetchConversation = async (
  otherUserId: string, 
  reagentId?: string, 
  limit: number = DEFAULT_LIMIT, 
  page: number = 0
): Promise<Message[]> => {
  try {
    // Get current user
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      toast.error("You must be logged in to view messages");
      return [];
    }

    const userId = sessionData.session.user.id;
    
    // Calculate offset for pagination
    const offset = page * limit;
    
    // Query for messages between the two users
    let query = supabase
      .from("messages")
      .select(`
        id,
        content,
        sender_id,
        recipient_id,
        reagent_id,
        read,
        created_at,
        payload,
        profiles!sender_id(username)
      `)
      .or(`and(sender_id.eq.${userId},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${userId})`)
      .order("created_at", { ascending: false }); // Get newest first for pagination
    
    // Filter by reagent if specified - very important to properly separate conversations
    if (reagentId) {
      query = query.eq("reagent_id", reagentId);
    } else {
      // If no reagent ID is specified, only get general messages (no reagent_id)
      query = query.is("reagent_id", null);
    }
    
    // Apply pagination
    query = query.limit(limit).range(offset, offset + limit - 1);

    const { data, error } = await query;

    if (error) {
      logger.error("Error fetching conversation:", error);
      toast.error("Failed to load conversation");
      return [];
    }

    // Mark unread messages as read
    const unreadMessages = (data || [])
      .filter(msg => msg.recipient_id === userId && !msg.read)
      .map(msg => msg.id);
    
    if (unreadMessages.length > 0) {
      logger.debug(`Marking ${unreadMessages.length} messages as read`);
      
      const { error: updateError } = await supabase
        .from("messages")
        .update({ read: true })
        .in("id", unreadMessages);
      
      if (updateError) {
        logger.error("Error marking messages as read:", updateError);
      }
    }

    // Transform the data to match our Message interface
    const messages = (data || []).map(msg => {
      // Access the sender profile data using the properly aliased join
      const profileData = msg.profiles 
        ? { username: msg.profiles.username }
        : undefined;
      
      return {
        ...msg,
        profiles: profileData
      };
    });

    // Reverse the array to get chronological order (oldest first)
    return messages.reverse();
  } catch (error) {
    logger.error("Exception in fetchConversation:", error);
    throw error; // Let the component handle the error
  }
};
